import React, { createContext, useContext, useState } from "react";
import _ from "lodash";

/**
 * @typedef {Object} AnswerUserFilesContext
 * @property {File[]} pictureFiles
 * @property {Function} setPictureFiles
 * @property {File[]} anyFiles
 * @property {Function} setAnyFiles
 * @property {RemoteMedia[]} homeworkUserDataPhotos
 * @property {Function} setHomeworkUserDataPhotos
 * @property {RemoteMedia[]} homeworkUserDataFiles
 * @property {Function} setHomeworkUserDataFiles
 * @property {FnResetFilesState} resetImage
 * @property {FnNumberVoid} removeImageFilesButtonHandler
 * @property {FnNumberVoid} removeHomeworkImageButtonHandler
 * @property {FnResetFilesState} resetFile
 * @property {FnNumberVoid} removeFilesButtonHandler
 * @property {FnNumberVoid} removeHomeworkFileButtonHandler
 * @property {UploadState[]} uploadStates - Массив состояний загрузки для каждого файла из pictureFiles.
 * @property {Boolean} isUploading - Флаг, указывающий что хотя бы одна фотография загружается.
 * @property {FnNumberProgressVoid} setUploadProgress
 * @property {FnNumberStatusVoid} setUploadStatus
 * @property {FnVoid} resetUploadStates
 */

/**
 * @callback FnResetFilesState
 * @param {ExerciseType7Answer|ExerciseType7Legacy} answer
 * @returns {void}
 */

/**
 * @typedef {Object} UploadState
 * @property {Number} progress - Процент загрузки (0-100).
 * @property {"idle"|"uploading"|"success"|"error"} status - Статус загрузки файла.
 */

/**
 * @callback FnNumberProgressVoid
 * @param {number} index - Индекс файла в pictureFiles.
 * @param {number} progress - Процент загрузки (0-100).
 * @returns {void}
 */

/**
 * @callback FnNumberStatusVoid
 * @param {number} index - Индекс файла в pictureFiles.
 * @param {"idle"|"uploading"|"success"|"error"} status - Статус загрузки.
 * @returns {void}
 */

/**
 * @callback FnVoid
 * @returns {void}
 */

/**
 * @type {React.Context<AnswerUserFilesContext>}
 */
const answerUserFilesContext = createContext({
  pictureFiles: [],
  setPictureFiles: () => {},
  anyFiles: [],
  setAnyFiles: () => {},
  homeworkUserDataPhotos: [],
  setHomeworkUserDataPhotos: () => {},
  homeworkUserDataFiles: [],
  setHomeworkUserDataFiles: () => {},
  resetImage: () => {},
  removeImageFilesButtonHandler: () => {},
  removeHomeworkImageButtonHandler: () => {},
  resetFile: () => {},
  removeFilesButtonHandler: () => {},
  removeHomeworkFileButtonHandler: () => {},
  uploadStates: [],
  isUploading: false,
  setUploadProgress: () => {},
  setUploadStatus: () => {},
  resetUploadStates: () => {},
});

/**
 * @returns {AnswerUserFilesContext}
 */
export const useAnswerUserFiles = () => useContext(answerUserFilesContext);

/**
 * @param {React.ReactNode} children
 * @returns {React.ReactNode}
 * @constructor
 */
const AnswerUserFilesProvider = ({ children }) => {
  const [pictureFiles, setPictureFiles] = useState([]);
  const [anyFiles, setAnyFiles] = useState([]);
  const [homeworkUserDataPhotos, setHomeworkUserDataPhotos] = useState([]);
  const [homeworkUserDataFiles, setHomeworkUserDataFiles] = useState([]);
  const [uploadStates, setUploadStates] = useState(/** @type {UploadState[]} */ []);

  /**
   * @param {ExerciseType7Answer|ExerciseType7Legacy} answer
   * @returns {void}
   */
  const resetImage = (answer) => {
    /** @type {Homework} */
    const exerciseHomework = _.get(answer, "homework");
    /** @type {HomeworkUserData} */
    const homeworkUserData = _.get(exerciseHomework, "userData");
    setPictureFiles([]);
    setHomeworkUserDataPhotos((homeworkUserData && homeworkUserData.photos) || []);
    setUploadStates([]);
  };

  /**
   * @param {ExerciseType7Answer|ExerciseType7Legacy} answer
   * @returns {void}
   */
  const resetFile = (answer) => {
    setAnyFiles([]);
    const _files = _.get(answer, "homework.userData.files");
    setHomeworkUserDataFiles((_files && [..._files]) || []);
  };

  /** @param {number} index */
  const removeImageFilesButtonHandler = (index) => () => {
    pictureFiles.splice(index, 1);
    setPictureFiles([...pictureFiles]);
    setUploadStates((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  };

  /**
   * Устанавливает процент загрузки для файла по индексу.
   * @param {number} index - Индекс файла в pictureFiles.
   * @param {number} progress - Процент загрузки (0-100).
   */
  const setUploadProgress = (index, progress) => {
    setUploadStates((prev) => {
      const next = [...prev];
      if (!next[index]) {
        next[index] = { progress: 0, status: "idle" };
      }
      next[index] = { ...next[index], progress };
      return next;
    });
  };

  /**
   * Устанавливает статус загрузки для файла по индексу.
   * @param {number} index - Индекс файла в pictureFiles.
   * @param {"idle"|"uploading"|"success"|"error"} status - Статус загрузки.
   */
  const setUploadStatus = (index, status) => {
    setUploadStates((prev) => {
      const next = [...prev];
      if (!next[index]) {
        next[index] = { progress: 0, status: "idle" };
      }
      next[index] = { ...next[index], status };
      return next;
    });
  };

  /** Сбрасывает все состояния загрузки. */
  const resetUploadStates = () => {
    setUploadStates([]);
  };

  const isUploading = uploadStates.some((s) => s && s.status === "uploading");

  /** @param {number} index */
  const removeHomeworkImageButtonHandler = (index) => () => {
    homeworkUserDataPhotos.splice(index, 1);
    setHomeworkUserDataPhotos([...homeworkUserDataPhotos]);
  };

  /** @param {number} index */
  const removeFilesButtonHandler = (index) => () => {
    anyFiles.splice(index, 1);
    setAnyFiles([...anyFiles]);
  };

  /** @param {number} index */
  const removeHomeworkFileButtonHandler = (index) => () => {
    homeworkUserDataFiles.splice(index, 1);
    setHomeworkUserDataFiles([...homeworkUserDataFiles]);
  };

  const value = {
    pictureFiles,
    setPictureFiles,
    homeworkUserDataPhotos,
    setHomeworkUserDataPhotos,
    anyFiles,
    setAnyFiles,
    homeworkUserDataFiles,
    setHomeworkUserDataFiles,
    resetImage,
    removeImageFilesButtonHandler,
    removeHomeworkImageButtonHandler,
    resetFile,
    removeFilesButtonHandler,
    removeHomeworkFileButtonHandler,
    uploadStates,
    isUploading,
    setUploadProgress,
    setUploadStatus,
    resetUploadStates,
  };

  return <answerUserFilesContext.Provider value={value}>{children}</answerUserFilesContext.Provider>;
};

export default AnswerUserFilesProvider;
